import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2, ShieldCheck, X } from "lucide-react";

import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SCOPE_LABELS = {
  "diary:read": "Read your listings, matters, hearing notes, to-dos and board notes",
  "diary:write": "File new entries, notes and to-dos (it will ask you in chat first)",
};

export default function ConnectClaude() {
  const [params] = useSearchParams();
  const requestId = params.get("request_id");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setError("This link is missing its connection request.");
      return;
    }
    api
      .get(`/oauth/authorize/request/${requestId}`)
      .then((res) => setInfo(res.data))
      .catch((e) => setError(errText(e) || "This connection request has expired."));
  }, [requestId]);

  const decide = async (allow) => {
    setBusy(true);
    try {
      const res = await api.post("/oauth/authorize/approve", { request_id: requestId, allow });
      window.location.href = res.data.redirect_url;
    } catch (e) {
      setError(errText(e) || "Could not complete the connection.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6" data-testid="connect-page">
      <div className="surface w-full max-w-lg p-8">
        {error ? (
          <div data-testid="connect-error">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <h1 className="mt-4 text-xl font-heading font-semibold">Connection not completed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              Start again from Claude and it will send you back here.
            </p>
          </div>
        ) : !info ? (
          <div className="flex justify-center py-10" data-testid="connect-loading">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div data-testid="connect-consent">
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h1 className="mt-4 text-xl font-heading font-semibold">
              Connect {info.client_name} to your diary?
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Signed in as <strong>{info.account_email}</strong>. This connects your own diary only.
            </p>

            <ul className="mt-6 space-y-3">
              {info.scopes.map((scope) => (
                <li key={scope} className="flex gap-3 text-sm" data-testid={`connect-scope-${scope}`}>
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>{SCOPE_LABELS[scope] || scope}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm text-muted-foreground">
              You can disconnect at any time from Settings → Connect Claude.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="connect-allow-btn"
                onClick={() => decide(true)}
                disabled={busy}
                className="rounded-full h-11 px-6 flex-1"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Allow
              </Button>
              <Button
                data-testid="connect-deny-btn"
                variant="outline"
                onClick={() => decide(false)}
                disabled={busy}
                className="rounded-full h-11 px-6"
              >
                <X className="h-4 w-4 mr-2" /> Not now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
